import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Wire schemas for the selector cabinet — Wave 2.63 BE-1.
 *
 * Three surfaces (all under `/selector/*`, RBAC = `@AdminSelectorOrAbove()`
 * scoped to the actor at the service layer):
 *   1. `GET /selector/stats/summary` — totals for the dashboard tiles
 *      (TASK-107a-followup-be).
 *   2. `GET /selector/stats/earnings` — time-series of accruals
 *      (TASK-107a-followup-be).
 *   3. `GET /selector/stats/activity` — recent moderation events
 *      (TASK-107a-followup-be).
 *   4. `POST /selector/tracks/:id/resubmit` — rejected-track resubmit
 *      (TASK-107b-followup-be-resubmit).
 *   5. `GET /selector/notifications` + `POST /:id/read` — selector inbox
 *      (TASK-107f-followup-be).
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const isoDateSchema = z.string().regex(ISO_DATE_RE, 'Date must be YYYY-MM-DD');
// ---------------------------------------------------------------------------
// 1. Stats summary
// ---------------------------------------------------------------------------
export const selectorStatsSummarySchema = z.object({
    tracks_approved: z.number().int().nonnegative(),
    presets_approved: z.number().int().nonnegative(),
    venues_using_my_presets: z.number().int().nonnegative(),
    earnings_current_period_kop: z.number().int().nonnegative(),
    earnings_last_period_kop: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// 2. Earnings series
// ---------------------------------------------------------------------------
export const selectorEarningsGranularitySchema = z.enum(['month', 'day']);
export const selectorEarningsFilterSchema = z
    .object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    granularity: selectorEarningsGranularitySchema.default('month'),
})
    .strict();
export const selectorEarningsPointSchema = z.object({
    bucket: isoDateSchema,
    amount_kop: z.number().int().nonnegative(),
});
export const selectorEarningsResponseSchema = z.object({
    granularity: selectorEarningsGranularitySchema,
    points: z.array(selectorEarningsPointSchema),
});
// ---------------------------------------------------------------------------
// 3. Recent activity (audit-log derived)
// ---------------------------------------------------------------------------
const ACTIVITY_LIMIT_DEFAULT = 20;
const ACTIVITY_LIMIT_MAX = 100;
export const selectorActivityFilterSchema = z
    .object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(ACTIVITY_LIMIT_MAX)
        .default(ACTIVITY_LIMIT_DEFAULT),
})
    .strict();
/**
 * Activity entry — derived from `internal.audit_log` rows scoped to the
 * selector. Only the actions a selector cares about are surfaced (track
 * approve / reject, preset approve / reject).
 */
export const selectorActivityActionSchema = z.enum([
    'track.approved',
    'track.rejected',
    'preset.approved',
    'preset.rejected',
]);
export const selectorActivityItemSchema = z.object({
    id: uuidSchema,
    occurred_at: isoDateTimeSchema,
    action: selectorActivityActionSchema,
    entity_type: z.enum(['track', 'preset']),
    entity_id: uuidSchema,
    entity_title: z.string().nullable(),
});
export const selectorActivityResponseSchema = z.object({
    items: z.array(selectorActivityItemSchema),
});
// ---------------------------------------------------------------------------
// 4. Rejected-track resubmit
// ---------------------------------------------------------------------------
const RESUBMIT_NOTE_MAX = 2000;
export const selectorTrackResubmitRequestSchema = z
    .object({
    master_upload_id: uuidSchema.optional(),
    change_note: z.string().trim().min(1).max(RESUBMIT_NOTE_MAX),
})
    .strict();
export const selectorTrackResubmitResponseSchema = z.object({
    id: uuidSchema,
    status: z.literal('ready_for_review'),
    resubmit_count: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// 5. Selector notifications inbox
// ---------------------------------------------------------------------------
//
// Wire shape MUST stay byte-compatible with the admin inbox
// (`notificationInboxItemSchema` in notification.ts) so the FE can share
// the same drawer component. The `recipient_user_id = current_user_id`
// filter is enforced server-side and never appears on the wire.
const SELECTOR_INBOX_CURSOR_MAX = 200;
export const selectorNotificationsFilterSchema = z
    .object({
    cursor: z.string().min(1).max(SELECTOR_INBOX_CURSOR_MAX).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    unread_only: z.coerce.boolean().optional().default(false),
})
    .strict();
export const selectorNotificationReadResponseSchema = z.object({
    id: uuidSchema,
    is_read: z.literal(true),
    read_at: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// 6. Duplicate replace (TASK-222-followup-be-flag)
// ---------------------------------------------------------------------------
//
// Admin moves the local "victim" track's venue interactions onto the
// duplicate target, then soft-deletes the victim. Body carries the chosen
// duplicate target id.
export const trackReplaceWithDuplicateRequestSchema = z
    .object({
    duplicate_track_id: uuidSchema,
})
    .strict();
export const trackReplaceWithDuplicateResponseSchema = z.object({
    id: uuidSchema,
    replaced_with_track_id: uuidSchema,
    moved_likes: z.number().int().nonnegative(),
    moved_dislikes: z.number().int().nonnegative(),
    moved_play_events: z.number().int().nonnegative(),
});
//# sourceMappingURL=selector-cabinet.js.map